import React, { useState, useEffect } from 'react';
import './PlayerEvaluationForm.css';
import { getErrorMessage } from '../utils/api';
import { buildApiUrl } from '../config/api';

interface PlayerEvaluationFormProps {
  onBackToLanding?: () => void;
  onViewDashboard?: (evaluationData: any) => void;
  onSaveSuccess?: (message: string) => void;
  evaluationId?: number | null;
  prefillPlayerInfo?: {
    first_name: string;
    last_name: string;
    age: number | string;
    position: string;
  };
  mode?: 'new' | 'edit';
}

interface SearchResult {
  player_id: number;
  first_name: string;
  last_name: string;
  age: number | null;
  position: string | null;
  has_evaluation: boolean;
  latest_evaluation_id: number | null;
  latest_evaluation_date: string | null;
  coach_has_evaluated: boolean;
}

// Using buildApiUrl helper instead of API_BASE_URL to ensure correct /api prefix

const INDICATOR_GROUPS = {
  Technical: ['first_touch', 'passing_accuracy', 'ball_control', 'use_of_both_feet', 'shooting'],
  Tactical: ['field_vision', 'decision_making', 'positioning', 'off_the_ball_movement', 'tackling'],
  Physical: ['speed', 'strength', 'endurance', 'height', 'weight', 'change_of_pace'],
  'Psycho-Social': ['resilience', 'communication', 'leadership', 'concentration', 'composure'],
};

const PlayerEvaluationForm: React.FC<PlayerEvaluationFormProps> = ({ 
  onBackToLanding, 
  onViewDashboard,
  onSaveSuccess,
  evaluationId,
  prefillPlayerInfo,
  mode = 'new'
}) => {
  const [playerInfo, setPlayerInfo] = useState({
    first_name: prefillPlayerInfo?.first_name || '',
    last_name: prefillPlayerInfo?.last_name || '',
    age: prefillPlayerInfo?.age?.toString() || '',
    position: prefillPlayerInfo?.position || '',
    gender: '',
  });

  const [scores, setScores] = useState<Record<string, string>>({
    first_touch: '',
    passing_accuracy: '',
    ball_control: '',
    use_of_both_feet: '',
    shooting: '',
    field_vision: '',
    decision_making: '',
    positioning: '',
    off_the_ball_movement: '',
    tackling: '',
    speed: '',
    strength: '',
    endurance: '',
    height: '',
    weight: '',
    change_of_pace: '',
    resilience: '',
    communication: '',
    leadership: '',
    concentration: '',
    composure: '',
  });

  const [enableAi, setEnableAi] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [aiFeedback, setAiFeedback] = useState<string | null>(null);
  
  // Search functionality
  const [showPlayerSearch, setShowPlayerSearch] = useState(true); // Default to visible
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Edit functionality
  const [isEditing, setIsEditing] = useState(mode === 'edit' || !!evaluationId);
  const [editingEvaluationId, setEditingEvaluationId] = useState<number | null>(evaluationId || null);

  // Prefill form when props change
  useEffect(() => {
    if (prefillPlayerInfo) {
      setPlayerInfo({
        first_name: prefillPlayerInfo.first_name || '',
        last_name: prefillPlayerInfo.last_name || '',
        age: prefillPlayerInfo.age?.toString() || '',
        position: prefillPlayerInfo.position || '',
        gender: '',
      });
    }
  }, [prefillPlayerInfo]);

  // Load evaluation if evaluationId is provided
  useEffect(() => {
    if (evaluationId) {
      console.log('Loading evaluation with ID:', evaluationId, 'Mode:', mode);
      // Use a local async function to load the evaluation
      const loadEval = async () => {
        const token = getToken();
        if (!token) {
          setError('Authentication required. Please log in again.');
          return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
          const response = await fetch(buildApiUrl(`/evaluations/${evaluationId}`), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Failed to load evaluation:', response.status, errorText);
            throw new Error(`Failed to load evaluation: ${response.status} ${errorText}`);
          }

          const data = await response.json();
          console.log('Evaluation data received:', data);
          const evalData = data.evaluation;
          
          if (!evalData) {
            console.error('No evaluation data in response:', data);
            throw new Error('No evaluation data received');
          }
          
          console.log('Populating form with:', {
            player: evalData.player,
            scores: evalData.scores
          });
          
          // Populate form with evaluation data
          setPlayerInfo({
            first_name: evalData.player?.first_name || '',
            last_name: evalData.player?.last_name || '',
            age: evalData.player?.age?.toString() || '',
            position: evalData.player?.position || '',
            gender: evalData.player?.gender || '',
          });

          // Convert scores to strings for form inputs
          const scoresAsStrings: Record<string, string> = {};
          if (evalData.scores && typeof evalData.scores === 'object') {
            for (const [key, value] of Object.entries(evalData.scores)) {
              if (value !== null && value !== undefined) {
                scoresAsStrings[key] = typeof value === 'number' ? value.toString() : String(value);
              } else {
                scoresAsStrings[key] = '';
              }
            }
          }
          console.log('Setting scores:', scoresAsStrings);
          setScores(scoresAsStrings);

          setIsEditing(true);
          setEditingEvaluationId(evaluationId);
          setShowPlayerSearch(false);
          setPlayerSearchQuery('');
          setSearchResults([]);
        } catch (err: any) {
          console.error('Error loading evaluation:', err);
          setError(getErrorMessage(err, 'Failed to load evaluation'));
        } finally {
          setIsSubmitting(false);
        }
      };
      
      loadEval();
    }
  }, [evaluationId, mode]);

  const getToken = (): string | null => {
    return localStorage.getItem('access_token');
  };

  const handlePlayerInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'last_name') {
      setPlayerInfo({ ...playerInfo, [name]: value.toUpperCase() });
    } else {
      setPlayerInfo({ ...playerInfo, [name]: value });
    }
  };

  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Special handling for height and weight
    if (name === 'height') {
      // Height: 100-220 cm (allow 2-3 digits)
      const numValue = parseFloat(value);
      if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 220)) {
        setScores({ ...scores, [name]: value });
      }
    } else if (name === 'weight') {
      // Weight: 20-100 kg (allow 2-3 digits)
      const numValue = parseFloat(value);
      if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 100)) {
        setScores({ ...scores, [name]: value });
      }
    } else {
      // All other indicators: 1-10 scale
      const numValue = parseFloat(value);
      if (value === '' || (!isNaN(numValue) && numValue >= 1 && numValue <= 10)) {
        setScores({ ...scores, [name]: value });
      }
    }
  };

  const handleSearchPlayers = async () => {
    if (!playerSearchQuery.trim() || playerSearchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setError(null);
    
    const token = getToken();
    if (!token) {
      setError('Authentication required. Please log in again.');
      setSearchLoading(false);
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(`/evaluations/search-players?query=${encodeURIComponent(playerSearchQuery)}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      if (data.success !== false) {
        setSearchResults(data.players || []);
        setError(null); // Clear any previous errors
      } else {
        setError(getErrorMessage(data, 'Search failed'));
        setSearchResults([]);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to search players'));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleLoadEvaluation = async (evaluationId: number, edit: boolean = false) => {
    const token = getToken();
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl(`/evaluations/${evaluationId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to load evaluation:', response.status, errorText);
        throw new Error(`Failed to load evaluation: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('Evaluation data received:', data);
      const evaluationData = data.evaluation;
      
      if (!evaluationData) {
        console.error('No evaluation data in response:', data);
        throw new Error('No evaluation data received');
      }
      
      console.log('Populating form with:', {
        player: evaluationData.player,
        scores: evaluationData.scores
      });
      
      // Populate form with evaluation data
      setPlayerInfo({
        first_name: evaluationData.player?.first_name || '',
        last_name: evaluationData.player?.last_name || '',
        age: evaluationData.player?.age?.toString() || '',
        position: evaluationData.player?.position || '',
        gender: evaluationData.player?.gender || '',
      });

      // Convert scores to strings for form inputs
      const scoresAsStrings: Record<string, string> = {};
      if (evaluationData.scores && typeof evaluationData.scores === 'object') {
        for (const [key, value] of Object.entries(evaluationData.scores)) {
          if (value !== null && value !== undefined) {
            scoresAsStrings[key] = typeof value === 'number' ? value.toString() : String(value);
          } else {
            scoresAsStrings[key] = '';
          }
        }
      }
      console.log('Setting scores:', scoresAsStrings);
      setScores(scoresAsStrings);

      setIsEditing(edit);
      setEditingEvaluationId(edit ? evaluationId : null);
      setShowPlayerSearch(false);
      setPlayerSearchQuery('');
      setSearchResults([]);

      if (!edit && onViewDashboard) {
        // Load dashboard with this evaluation
        onViewDashboard(evaluationData);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load evaluation'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExitEdit = () => {
    if (window.confirm('Are you sure you want to exit? Any unsaved changes will be lost.')) {
      // Clear form
      setPlayerInfo({
        first_name: '',
        last_name: '',
        age: '',
        position: '',
        gender: '',
      });
      setScores({
        first_touch: '',
        passing_accuracy: '',
        ball_control: '',
        use_of_both_feet: '',
        shooting: '',
        field_vision: '',
        decision_making: '',
        positioning: '',
        off_the_ball_movement: '',
        tackling: '',
        speed: '',
        strength: '',
        endurance: '',
        height: '',
        weight: '',
        change_of_pace: '',
        resilience: '',
        communication: '',
        leadership: '',
        concentration: '',
        composure: '',
      });
      setEnableAi(true);
      setIsEditing(false);
      setEditingEvaluationId(null);
      setShowPlayerSearch(false);
      setPlayerSearchQuery('');
      setSearchResults([]);
      setError(null);
      setSuccess(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Validate player info
      if (!playerInfo.first_name || !playerInfo.last_name || !playerInfo.age || !playerInfo.position) {
        throw new Error('Please fill in all player information (First Name, Last Name, Age, and Position)');
      }

      // Validate age is a valid number
      const playerAge = parseInt(playerInfo.age, 10);
      if (isNaN(playerAge) || playerAge < 1 || playerAge > 18) {
        throw new Error('Please enter a valid age between 1 and 18');
      }

      // Validate all scores are filled
      const missingScores = Object.entries(scores).filter(([_, value]) => !value || value === '');
      if (missingScores.length > 0) {
        throw new Error(`Please fill in all scores. Missing: ${missingScores.map(([key]) => key.replace('_', ' ')).join(', ')}`);
      }

      // Convert scores to numbers
      const scoresNumeric: Record<string, number> = {};
      for (const [key, value] of Object.entries(scores)) {
        scoresNumeric[key] = parseFloat(value);
      }

      const token = getToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      // First, evaluate to get scores and AI feedback
      const evaluateResponse = await fetch(buildApiUrl('/evaluate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player: {
            first_name: playerInfo.first_name,
            last_name: playerInfo.last_name,
            age: playerAge,
            position: playerInfo.position,
            gender: playerInfo.gender,
          },
          scores: scoresNumeric,
          generate_ai_insights: enableAi,
        }),
      });

      if (!evaluateResponse.ok) {
        const errorData = await evaluateResponse.json();
        throw new Error(getErrorMessage(errorData, 'Evaluation failed'));
      }

      const evaluationResult = await evaluateResponse.json();

      if (isEditing && editingEvaluationId) {
        // Update existing evaluation
        const updateResponse = await fetch(buildApiUrl(`/evaluations/${editingEvaluationId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            player: {
              first_name: playerInfo.first_name,
              last_name: playerInfo.last_name,
              age: Number(playerInfo.age),
              position: playerInfo.position,
              gender: playerInfo.gender,
            },
            scores: scoresNumeric,
            category_scores: evaluationResult.category_scores,
            weighted_index: evaluationResult.weighted_index,
            ai_feedback: enableAi ? evaluationResult.ai_feedback : null,
            created_at: enableAi ? new Date().toISOString() : undefined,
          }),
        });

        if (!updateResponse.ok) {
          let errorText = '';
          try {
            const errorData = await updateResponse.json();
            errorText = getErrorMessage(errorData, 'Failed to update evaluation');
          } catch {
            errorText = await updateResponse.text();
          }
          console.error('Failed to update evaluation:', updateResponse.status, errorText);
          throw new Error(errorText || 'Failed to update evaluation');
        }

        setSuccess('Evaluation updated successfully!');
        setIsEditing(false);
        setEditingEvaluationId(null);
        if (onSaveSuccess) {
          onSaveSuccess('Your Player Evaluation has been saved!');
        }
      } else {
        // Save new evaluation
        const saveResponse = await fetch(buildApiUrl('/evaluations'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            player_first_name: playerInfo.first_name,
            player_last_name: playerInfo.last_name,
            player_age: parseInt(playerInfo.age, 10) || 0,
            player_position: playerInfo.position,
            player_gender: playerInfo.gender,
            scores: scoresNumeric,
            category_scores: evaluationResult.category_scores,
            weighted_index: evaluationResult.weighted_index,
            ai_feedback: enableAi ? evaluationResult.ai_feedback : null,
          }),
        });

        if (!saveResponse.ok) {
          let errorText = '';
          try {
            const errorData = await saveResponse.json();
            errorText = getErrorMessage(errorData, 'Failed to save evaluation');
          } catch {
            errorText = await saveResponse.text();
          }
          console.error('Failed to save evaluation:', saveResponse.status, errorText);
          throw new Error(errorText);
        }

        const saveResult = await saveResponse.json();
        console.log('Evaluation saved:', saveResult);
        
        // Build success message with AI feedback info
        let successMessage = `✓ Evaluation saved successfully! (ID: ${saveResult.id})`;
        if (enableAi && evaluationResult.ai_feedback) {
          successMessage += ' AI feedback has been generated below.';
          setAiFeedback(evaluationResult.ai_feedback);
        } else {
          setAiFeedback(null);
        }
        setSuccess(successMessage);
        
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Call success callback if provided
        if (onSaveSuccess) {
          onSaveSuccess('Evaluation Saved!');
        }
        
        // If dashboard callback provided, navigate to dashboard
        if (onViewDashboard) {
          onViewDashboard(evaluationResult);
        } else {
          // Reset form after 5 seconds to give user time to see success message and AI feedback
      setTimeout(() => {
        setPlayerInfo({
              first_name: '',
              last_name: '',
              age: '',
              position: '',
              gender: '',
        });
        setScores({
          first_touch: '',
          passing_accuracy: '',
          ball_control: '',
          field_vision: '',
          decision_making: '',
          positioning: '',
          speed: '',
          strength: '',
          endurance: '',
          resilience: '',
          communication: '',
          leadership: '',
        });
        setSuccess(null);
            setAiFeedback(null);
          }, 5000);
        }
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'An error occurred while submitting the evaluation'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderScoreInput = (name: string, label: string) => {
    // Special handling for height and weight
    const isHeight = name === 'height';
    const isWeight = name === 'weight';
    const min = isHeight ? 100 : isWeight ? 20 : 1;
    const max = isHeight ? 220 : isWeight ? 100 : 10;
    const step = isHeight || isWeight ? 0.1 : 0.1;
    const unit = isHeight ? ' cm' : isWeight ? ' kg' : '';
    
    return (
      <div className="score-input-group">
        <label htmlFor={name} className="indicator-label">
          <span className="indicator-label-text">{label}{unit}</span>
        </label>
        <input
          type="number"
          id={name}
          name={name}
          value={scores[name]}
          onChange={handleScoreChange}
          min={min}
          max={max}
          step={step}
          required
          className="score-input"
        />
      </div>
    );
  };

  return (
    <div className="evaluation-form-page">
      <div className="evaluation-header" style={{ position: 'relative' }}>
        <h1>Player Performance Evaluation</h1>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Are you sure you want to leave? Any unsaved data will be lost.')) {
              onBackToLanding?.();
            }
          }}
          className="close-button"
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: 'transparent',
            border: '1px solid rgba(86, 122, 216, 0.5)',
            color: '#567ad8',
            borderRadius: '8px',
            padding: '0.6rem 1.2rem',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          ✕ Close
        </button>
      </div>
      
      <div className="evaluation-container">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ 
            marginBottom: aiFeedback ? '1rem' : '0',
            fontSize: '1.1rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1.25rem 1.5rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>✓</span>
            <span>{success}</span>
          </div>
        )}
        {aiFeedback && (
          <div style={{
            backgroundColor: '#1f2230',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '2px solid #567ad8',
            marginBottom: '1.5rem',
            marginTop: success ? '0' : '1.5rem'
          }}>
            <h3 style={{ color: '#567ad8', marginTop: 0, marginBottom: '1rem' }}>
              AI-Generated Feedback
            </h3>
            <div style={{
              color: '#e0e0e0',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap'
            }}>
              {aiFeedback}
            </div>
          </div>
        )}

        {/* Search For Player Section */}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#1f2230', borderRadius: '8px', border: '1px solid #383b48' }}>
          <button
            type="button"
            onClick={() => {
              setShowPlayerSearch(!showPlayerSearch);
              if (!showPlayerSearch) {
                setError(null); // Clear errors when opening search
              }
            }}
            className="button primary"
            style={{ width: '100%', marginBottom: showPlayerSearch ? '1rem' : '0' }}
          >
            {showPlayerSearch ? '▼ Hide Search' : '🔍 Search For Player'}
          </button>
          
          {showPlayerSearch && (
              <div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    placeholder="Search by player name..."
                    value={playerSearchQuery}
                    onChange={(e) => setPlayerSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchPlayers()}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #383b48',
                      backgroundColor: '#11121b',
                      color: '#fdfeff',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSearchPlayers}
                    disabled={searchLoading}
                    className="button primary"
                  >
                    {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>

                {searchResults.length > 0 && (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {searchResults.map((player) => (
                      <div
                        key={player.player_id}
                        style={{
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          backgroundColor: '#11121b',
                          borderRadius: '6px',
                          border: '1px solid #383b48',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong>{player.first_name} {player.last_name}</strong>
                            {player.position && <span style={{ marginLeft: '0.5rem', color: '#567ad8' }}> - {player.position}</span>}
                            {player.age && <span style={{ marginLeft: '0.5rem', color: '#797b84' }}> (Age: {player.age})</span>}
                            {player.has_evaluation && (
                              <span style={{ marginLeft: '1rem', color: '#4ade80' }}>
                                ✓ Has evaluation {player.latest_evaluation_date && `(${new Date(player.latest_evaluation_date).toLocaleDateString()})`}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {player.has_evaluation && player.latest_evaluation_id && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleLoadEvaluation(player.latest_evaluation_id!, true)}
                                  className="button primary small"
                                >
                                  Load & Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleLoadEvaluation(player.latest_evaluation_id!, false)}
                                  className="button ghost small"
                                >
                                  Load
                                </button>
                              </>
                            )}
                            {!player.has_evaluation && (
                              <button
                                type="button"
                                onClick={() => {
                                  setPlayerInfo({
                                    first_name: player.first_name,
                                    last_name: player.last_name,
                                    age: player.age?.toString() || '',
                                    position: player.position || '',
                                    gender: '',
                                  });
                                  setShowPlayerSearch(false);
                                  setPlayerSearchQuery('');
                                  setSearchResults([]);
                                }}
                                className="button primary small"
                              >
                                New Evaluation
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        {/* Editing Banner */}
        {isEditing && (
          <div           style={{
            padding: '1rem',
            backgroundColor: '#1f2230',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '2px solid #567ad8',
          }}>
            <span style={{ color: '#567ad8', fontWeight: '600' }}>
              ✏️ Editing: {playerInfo.first_name} {playerInfo.last_name}
            </span>
            <button
              type="button"
              onClick={handleExitEdit}
              className="button ghost small"
            >
              Exit
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-section">
            <h2>Player Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={playerInfo.first_name}
                  onChange={handlePlayerInfoChange}
                  required
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={playerInfo.last_name}
                  onChange={handlePlayerInfoChange}
                  required
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Age *</label>
                <input
                  type="number"
                  name="age"
                  value={playerInfo.age}
                  onChange={handlePlayerInfoChange}
                  required
                  min="1"
                  max="18"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>Gender *</label>
                <select
                  name="gender"
                  value={playerInfo.gender}
                  onChange={handlePlayerInfoChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Position *</label>
                <select
                  name="position"
                  value={playerInfo.position}
                  onChange={handlePlayerInfoChange}
                  required
                >
                  <option value="">Select Position</option>
                  <option value="Goalkeeper">Goalkeeper</option>
                  <option value="Defender">Defender</option>
                  <option value="Midfielder">Midfielder</option>
                  <option value="Forward">Forward</option>
                </select>
              </div>
            </div>
          </div>

          {/* Indicator Scores - Two Column Layout */}
          <div className="form-section">
            <h2>Indicator Scores</h2>
            <p className="section-description">Select a whole number between 1 – 10 for each indicator.</p>
            <div className="indicator-grid-three-columns">
              {Object.entries(INDICATOR_GROUPS).map(([category, indicators]) => (
                <div key={category} style={{ gridColumn: 'span 2' }}>
                  <h3 style={{ color: '#567ad8', marginBottom: '1rem' }}>{category}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {indicators.map((indicator) => {
                      const label = indicator.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ');
                      return renderScoreInput(indicator, label);
                    })}
            </div>
          </div>
              ))}
            </div>
          </div>

          {/* AI Feedback Option */}
          <div className="form-section">
            <h2>AI Feedback</h2>
            <p className="section-description">Generate AI-powered performance analysis and recommendations</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="enable-ai"
                checked={enableAi}
                onChange={(e) => setEnableAi(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              <label htmlFor="enable-ai" style={{ margin: 0, cursor: 'pointer' }}>
                Generate AI-powered feedback and recommendations
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (isEditing ? 'Saving...' : 'Evaluating...') : (isEditing ? 'Save Changes' : 'Evaluate Player')}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={handleExitEdit}
                className="button ghost"
              >
                Exit
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlayerEvaluationForm;
